import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { toolTypeApi, ToolType, ToolTypeSearchParams } from '../../services/toolTypeApi';
import { RootState } from '../reducers';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setBulkLoading, setSuccessMessage,
  FETCH_TOOLTYPE, FETCH_TOOLTYPE_BY_ID, CREATE_TOOLTYPE, UPDATE_TOOLTYPE, DELETE_TOOLTYPE,
  SEARCH_TOOLTYPE, BULK_ACTIVATE_TOOLTYPE, BULK_DEACTIVATE_TOOLTYPE, BULK_DELETE_TOOLTYPE,
  EXPORT_TOOLTYPE_CSV
} from '../reducers/toolTypeSlice';

function* fetchSaga(action: PayloadAction<ToolTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.toolType;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(toolTypeApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to fetch Tool Types')); }
  finally { yield put(setLoading(false)); }
}

function* fetchByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const item = yield call(toolTypeApi.getById, action.payload); yield put(setSelectedItem(item)); }
  catch (e: any) { yield put(setError(e.message || 'Failed to fetch Tool Type')); }
  finally { yield put(setLoading(false)); }
}

function* createSaga(action: PayloadAction<Omit<ToolType, 'Id'>>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const errors = yield call(toolTypeApi.validation.validate, action.payload); if (errors.length) { yield put(setValidationErrors(errors)); return; } const created = yield call(toolTypeApi.create, action.payload); yield put(addItem(created)); yield put(setSuccessMessage('Created successfully')); }
  catch (e: any) { yield put(setError(e.message || 'Failed to create Tool Type')); }
  finally { yield put(setLoading(false)); }
}

function* updateSaga(action: PayloadAction<{ id: number; item: Partial<ToolType> }>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const { id, item } = action.payload; const errors = yield call(toolTypeApi.validation.validate, item); if (errors.length) { yield put(setValidationErrors(errors)); return; } const updated = yield call(toolTypeApi.update, id, item); yield put(updateItem(updated)); yield put(setSuccessMessage('Updated successfully')); }
  catch (e: any) { yield put(setError(e.message || 'Failed to update Tool Type')); }
  finally { yield put(setLoading(false)); }
}

function* deleteSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const resp = yield call(toolTypeApi.delete, action.payload); if (resp.success) { yield put(removeItem(action.payload)); yield put(setSuccessMessage('Deleted successfully')); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  catch (e: any) { yield put(setError(e.message || 'Failed to delete Tool Type')); }
  finally { yield put(setLoading(false)); }
}

function* searchSaga(action: PayloadAction<ToolTypeSearchParams>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const resp = yield call(toolTypeApi.search, action.payload); yield put(setItems(resp.data)); yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 })); }
  catch (e: any) { yield put(setError(e.message || 'Failed to search Tool Types')); }
  finally { yield put(setLoading(false)); }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(toolTypeApi.bulkActivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_TOOLTYPE }); } else { yield put(setError(resp.message || 'Failed to activate')); } }
  finally { yield put(setBulkLoading(false)); }
}
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(toolTypeApi.bulkDeactivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_TOOLTYPE }); } else { yield put(setError(resp.message || 'Failed to deactivate')); } }
  finally { yield put(setBulkLoading(false)); }
}
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(toolTypeApi.bulkDelete, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_TOOLTYPE }); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  finally { yield put(setBulkLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<ToolTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.toolType.searchParams, ...action.payload };
    const blob = yield call(toolTypeApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `tooltype_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to export Tool Types')); }
  finally { yield put(setLoading(false)); }
}

export function* toolTypeSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_TOOLTYPE, fetchSaga);
  yield takeEvery(FETCH_TOOLTYPE_BY_ID, fetchByIdSaga);
  yield takeEvery(CREATE_TOOLTYPE, createSaga);
  yield takeEvery(UPDATE_TOOLTYPE, updateSaga);
  yield takeEvery(DELETE_TOOLTYPE, deleteSaga);
  yield takeEvery(SEARCH_TOOLTYPE, searchSaga);
  yield takeEvery(BULK_ACTIVATE_TOOLTYPE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE_TOOLTYPE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE_TOOLTYPE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TOOLTYPE_CSV, exportCsvSaga);
} 